import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import BenefitsSummary from "./BenefitsSummary";
import { ChildBenefits, PlanBenefits } from "./benefitsSummary.api";
import {
  BENEFITS_TEAM_PHONE,
  childDisplayName,
  planLabel,
  toBenefitsSummaryData,
} from "./benefitsSummary.mapper";
import { useBenefitsSummary } from "./useBenefitsSummary";
import { useMessageBenefitsTeam } from "./useMessageBenefitsTeam";
import MessageCard from "../../../components/patientPortal/MessageCard";
import PageFrame from "../../../components/patientPortal/PageFrame";

// The comma makes the phone wait for the call to connect before it dials the
// extension.
const toTelHref = (phone: string) => {
  const [number, extension] = phone.split("ext.");
  const dial = (part: string) => part.replace(/[^\d+]/g, "");
  return extension ? `tel:${dial(number)},${dial(extension)}` : `tel:${dial(number)}`;
};

const CallButton = () => (
  <Button
    variant="outlined"
    startIcon={<PhoneOutlinedIcon />}
    href={toTelHref(BENEFITS_TEAM_PHONE)}
  >
    Call {BENEFITS_TEAM_PHONE}
  </Button>
);

const LoadingState = () => (
  <PageFrame>
    <Stack spacing={3} aria-busy="true" aria-label="Loading benefits summary">
      <Skeleton variant="text" width="50%" height={40} />
      <Skeleton variant="rounded" height={140} />
      <Skeleton variant="rounded" height={360} />
    </Stack>
  </PageFrame>
);

const CompleteState = ({ child, plan }: { child: ChildBenefits; plan: PlanBenefits }) => {
  const sendMessage = useMessageBenefitsTeam(child.clientId, plan.id);
  return <BenefitsSummary data={toBenefitsSummaryData(child, plan)} onSendMessage={sendMessage} />;
};

const WaitingState = ({ child, plan }: { child: ChildBenefits; plan: PlanBenefits }) => (
  <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
    <MessageCard
      title={`We're checking ${childDisplayName(child)}'s benefits`}
      icon={<HourglassEmptyOutlinedIcon />}
    >
      <Typography variant="body2" color="text.secondary">
        We've asked {plan.payerName || "your insurance provider"} about your plan. This
        usually takes one to two business days, and your summary will appear here
        when it's ready.
      </Typography>
    </MessageCard>
  </Box>
);

const NoInsuranceState = ({ name }: { name?: string }) => (
  <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
    <MessageCard title="No benefits summary yet">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {name
          ? `We don't have insurance on file for ${name} yet.`
          : "We couldn't find a benefits summary for your account."}{" "}
        If you think this is a mistake, our benefits team can help.
      </Typography>
      <CallButton />
    </MessageCard>
  </Box>
);

const BenefitsSummaryPage = () => {
  const { state, retry } = useBenefitsSummary();
  // The selected child is kept in the URL (?child=<clientId>), so a refresh
  // or a shared link opens the same child.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClientId = Number(searchParams.get("child"));
  const [planIndex, setPlanIndex] = useState(0);

  // A different child has a different list of plans.
  useEffect(() => setPlanIndex(0), [selectedClientId]);

  if (state.status === "loading") return <LoadingState />;

  if (state.status === "error") {
    return (
      <PageFrame>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Try again
            </Button>
          }
        >
          We couldn't load your benefits summary right now. Please try again in a
          few minutes.
        </Alert>
      </PageFrame>
    );
  }

  const { children } = state.data;
  if (children.length === 0) return <NoInsuranceState />;

  // Children arrive sorted by name; the first is the default.
  const child =
    children.find((option) => option.clientId === selectedClientId) ?? children[0];
  const plan = child.plans[Math.min(planIndex, child.plans.length - 1)];

  const selectors = (children.length > 1 || child.plans.length > 1) && (
    <PageFrame>
      <Stack spacing={2}>
        {children.length > 1 && (
          <TextField
            select
            size="small"
            label="Child"
            value={child.clientId}
            onChange={(event) =>
              setSearchParams(
                (params) => {
                  params.set("child", String(event.target.value));
                  return params;
                },
                { replace: true }
              )
            }
            sx={{ maxWidth: 320 }}
          >
            {children.map((option) => (
              <MenuItem key={option.clientId} value={option.clientId}>
                {childDisplayName(option)}
              </MenuItem>
            ))}
          </TextField>
        )}
        {child.plans.length > 1 && (
          <Tabs
            value={planIndex}
            onChange={(_event, value: number) => setPlanIndex(value)}
            aria-label="Insurance plans"
          >
            {child.plans.map((option, index) => (
              <Tab key={option.id} label={planLabel(option, index)} />
            ))}
          </Tabs>
        )}
      </Stack>
    </PageFrame>
  );

  return (
    <>
      {selectors}
      {!plan ? (
        <NoInsuranceState name={childDisplayName(child)} />
      ) : plan.status === "Waiting" ? (
        <WaitingState child={child} plan={plan} />
      ) : (
        <CompleteState child={child} plan={plan} />
      )}
    </>
  );
};

export default BenefitsSummaryPage;
